/**
 * Entity Comments Store
 *
 * Threaded discussion for any entity: item, cycle, track, session, or tool.
 * Backed by the `entity_comments` Supabase table — the one discussion table
 * (the item page's `item_comments` was folded into it by migration 815).
 *
 * What the server does on insert (815, trg_notify_entity_comment): the people
 * in `mentions` get a `mention` notification, the thread's other authors get a
 * `comment_reply`, and — where the environment has an integrations engine —
 * the comment reaches Slack. This store never sends notifications itself.
 *
 * Persistence: Supabase only (no localStorage cache).
 */

import { create } from 'zustand';
import {
  fetchAll,
  isSupabaseAvailable,
  createInitialSyncState,
  generateUUID,
  type SyncState,
} from '../../lib/supabaseSync';
import { getTypedSupabaseClient } from '../../lib/typedSupabaseClient';
import { dispatchStoreError } from '../../hooks/prioritization/useStoreErrorHandler';
import type {
  CommentAnchor,
  EntityComment,
  EntityCommentEntityType,
} from '../../types/prioritization/entityComments';
import {
  resolveAuthorship,
  legacyFromAuthorship,
} from '../../types/prioritization/creationProvenance';

export type { EntityComment, EntityCommentEntityType, CommentAnchor };

const TABLE_NAME = 'entity_comments';

// ============================================================================
// Row serialisation
// ============================================================================

function commentToRow(c: EntityComment) {
  return {
    id: c.id,
    workspace_id: c.workspaceId,
    entity_type: c.entityType,
    entity_id: c.entityId,
    author_id: c.authorId,
    author_name: c.authorName,
    body: c.body,
    reply_to_id: c.replyToId ?? null,
    is_edited: c.isEdited,
    created_at: c.createdAt instanceof Date ? c.createdAt.toISOString() : c.createdAt,
    mentions: c.mentions,
    anchor_type: c.anchorType,
    anchor_key: c.anchorKey,
    anchor_label: c.anchorLabel,
    author_principal_type: c.authorPrincipalType ?? 'user',
    voice_id: c.voiceId ?? null,
    creation_source: c.creationSource ?? (c.authorPrincipalType === 'voice' ? 'ai' : 'human'),
    creation_review_status: c.creationReviewStatus ?? 'none',
    creation_provenance: c.creationProvenance ?? 'human',
  };
}

function fromRow(row: Record<string, unknown>): EntityComment {
  const toDate = (v: unknown) =>
    v instanceof Date ? v : v ? new Date(v as string) : undefined;
  const rawPrincipal = row.authorPrincipalType ?? row.author_principal_type;
  const principal: 'user' | 'voice' | 'agent' =
    rawPrincipal === 'voice' ? 'voice' : rawPrincipal === 'agent' ? 'agent' : 'user';
  const authorship = resolveAuthorship({
    source: (row.creationSource ?? row.creation_source) as 'human' | 'ai' | 'automated' | undefined,
    reviewStatus: (row.creationReviewStatus ?? row.creation_review_status) as
      | 'none'
      | 'human_reviewed'
      | undefined,
    provenance: (row.creationProvenance ?? row.creation_provenance) as string | undefined,
  });
  // A Voice's or an agent's words are the AI's even when an older row says otherwise.
  const effective =
    (principal === 'voice' || principal === 'agent') && authorship.source === 'human'
      ? { source: 'ai' as const, reviewStatus: authorship.reviewStatus }
      : authorship;
  const mentionsRaw = row.mentions;
  const anchorType = (row.anchorType ?? row.anchor_type) as string | null | undefined;
  return {
    id: row.id as string,
    workspaceId: (row.workspaceId ?? row.workspace_id) as string,
    entityType: (row.entityType ?? row.entity_type) as EntityCommentEntityType,
    entityId: (row.entityId ?? row.entity_id) as string,
    authorId: (row.authorId ?? row.author_id) as string,
    authorName: (row.authorName ?? row.author_name) as string,
    body: row.body as string,
    replyToId: ((row.replyToId ?? row.reply_to_id) as string | null) ?? null,
    isEdited: Boolean(row.isEdited ?? row.is_edited),
    createdAt: toDate(row.createdAt ?? row.created_at) ?? new Date(),
    updatedAt: toDate(row.updatedAt ?? row.updated_at) ?? null,
    mentions: Array.isArray(mentionsRaw) ? (mentionsRaw as string[]) : [],
    anchorType: anchorType === 'attribute' ? 'attribute' : null,
    anchorKey: ((row.anchorKey ?? row.anchor_key) as string | null) ?? null,
    anchorLabel: ((row.anchorLabel ?? row.anchor_label) as string | null) ?? null,
    resolvedAt: toDate(row.resolvedAt ?? row.resolved_at) ?? null,
    resolvedBy: ((row.resolvedBy ?? row.resolved_by) as string | null) ?? null,
    authorPrincipalType: principal,
    voiceId: ((row.voiceId ?? row.voice_id) as string | null) ?? null,
    creationSource: effective.source,
    creationReviewStatus: effective.reviewStatus,
    creationProvenance: legacyFromAuthorship(effective),
  };
}

function sameEntity(c: EntityComment, entityType: EntityCommentEntityType, entityId: string) {
  return c.entityType === entityType && c.entityId === entityId;
}

// ============================================================================
// Store types
// ============================================================================

export interface AddCommentParams {
  entityType: EntityCommentEntityType;
  entityId: string;
  workspaceId: string;
  body: string;
  authorId: string;
  authorName: string;
  replyToId?: string;
  /** auth.users ids named in the body (see serializeMentions). */
  mentions?: string[];
  /** The value this comment is about, when it is about one. */
  anchor?: CommentAnchor | null;
  /** The app writes user or voice comments; agent comments arrive only through the public API. */
  authorPrincipalType?: 'user' | 'voice';
  voiceId?: string | null;
  creationSource?: import('../../types/prioritization/creationProvenance').CreationSource;
  creationReviewStatus?: import('../../types/prioritization/creationProvenance').CreationReviewStatus;
  creationProvenance?: import('../../types/prioritization/creationProvenance').CreationProvenance;
}

export interface ThreadCounts {
  open: number;
  resolved: number;
}

interface EntityCommentsStoreState {
  comments: EntityComment[];
  syncState: SyncState;

  /** Everything the person may read. Kept for callers that want the whole set. */
  fetchFromSupabase: () => Promise<void>;
  /** The discussion on one entity; replaces that entity's slice of the cache. */
  fetchForEntity: (entityType: EntityCommentEntityType, entityId: string) => Promise<void>;

  addComment: (params: AddCommentParams) => string;
  updateComment: (commentId: string, body: string, mentions?: string[]) => void;
  deleteComment: (commentId: string) => void;
  /** Any workspace member may close or reopen a root comment's thread. */
  setThreadResolved: (commentId: string, resolved: boolean, byUserId: string) => void;

  getCommentsForEntity: (entityType: EntityCommentEntityType, entityId: string) => EntityComment[];
  getReplies: (commentId: string) => EntityComment[];
  getCommentCount: (entityType: EntityCommentEntityType, entityId: string) => number;
  getThreadCounts: (
    entityType: EntityCommentEntityType,
    entityId: string,
    anchorKey?: string,
  ) => ThreadCounts;
}

// ============================================================================
// Store
// ============================================================================

export const useEntityCommentsStore = create<EntityCommentsStoreState>()(
  (set, get) => ({
    comments: [],
    syncState: createInitialSyncState(),

    // -------------------------------------------------------------------------
    fetchFromSupabase: async () => {
      if (!isSupabaseAvailable()) return;

      set({ syncState: { ...get().syncState, isLoading: true, syncError: null } });

      const result = await fetchAll<Record<string, unknown>>(TABLE_NAME, {
        column: 'created_at',
        ascending: false,
      });

      if (result.error) {
        if (
          result.error.includes('does not exist') ||
          result.error.includes('42P01')
        ) {
          set({ syncState: { ...get().syncState, isLoading: false, syncError: null } });
          return;
        }
        set({ syncState: { ...get().syncState, isLoading: false, syncError: result.error } });
        console.error('[EntityCommentsStore] fetch error:', result.error);
        return;
      }

      if (result.data) {
        const comments = result.data.map(fromRow);
        set({
          comments,
          syncState: {
            ...get().syncState,
            isLoading: false,
            lastSyncAt: new Date(),
            syncError: null,
          },
        });
      }
    },

    // -------------------------------------------------------------------------
    fetchForEntity: async (entityType, entityId) => {
      if (!isSupabaseAvailable() || !entityId) return;
      set({ syncState: { ...get().syncState, isLoading: true, syncError: null } });

      const db = getTypedSupabaseClient();
      const { data, error } = await db
        .from(TABLE_NAME)
        .select('*')
        .eq('entity_type', entityType)
        .eq('entity_id', entityId)
        .order('created_at', { ascending: false });

      if (error) {
        set({ syncState: { ...get().syncState, isLoading: false, syncError: error.message } });
        console.error('[EntityCommentsStore] fetchForEntity error:', error.message);
        return;
      }

      const fresh = (data ?? []).map((row) => fromRow(row as unknown as Record<string, unknown>));
      set((state) => ({
        comments: [
          ...state.comments.filter((c) => !sameEntity(c, entityType, entityId)),
          ...fresh,
        ],
        syncState: { ...state.syncState, isLoading: false, lastSyncAt: new Date(), syncError: null },
      }));
    },

    // -------------------------------------------------------------------------
    addComment: ({
      entityType,
      entityId,
      workspaceId,
      body,
      authorId,
      authorName,
      replyToId,
      mentions,
      anchor,
      authorPrincipalType,
      voiceId,
      creationSource,
      creationReviewStatus,
      creationProvenance,
    }) => {
      const id = generateUUID();
      const authorship = resolveAuthorship({
        source: creationSource,
        reviewStatus: creationReviewStatus,
        provenance:
          creationProvenance ??
          (authorPrincipalType === 'voice' ? 'ai_draft' : 'human'),
      });
      const comment: EntityComment = {
        id,
        workspaceId,
        entityType,
        entityId,
        authorId,
        authorName,
        body,
        replyToId: replyToId ?? null,
        isEdited: false,
        createdAt: new Date(),
        updatedAt: null,
        mentions: Array.from(new Set((mentions ?? []).filter((m) => m && m !== authorId))),
        anchorType: anchor ? anchor.type : null,
        anchorKey: anchor ? anchor.key : null,
        anchorLabel: anchor?.label ?? null,
        resolvedAt: null,
        resolvedBy: null,
        authorPrincipalType: authorPrincipalType ?? 'user',
        voiceId: voiceId ?? null,
        creationSource: authorship.source,
        creationReviewStatus: authorship.reviewStatus,
        creationProvenance: legacyFromAuthorship(authorship),
      };

      set((state) => ({ comments: [comment, ...state.comments] }));

      if (isSupabaseAvailable()) {
        const db = getTypedSupabaseClient();
        void db
          .from(TABLE_NAME)
          .insert(commentToRow(comment))
          .then(({ error }) => {
            if (error) {
              console.error('[EntityCommentsStore] insert error:', error.message);
              set((state) => ({ comments: state.comments.filter((c) => c.id !== id) }));
              dispatchStoreError('EntityComments', 'post comment', error.message);
            }
          });
      }

      return id;
    },

    // -------------------------------------------------------------------------
    updateComment: (commentId, body, mentions) => {
      const previous = get().comments.find((c) => c.id === commentId);
      if (!previous) return;
      const now = new Date();
      const nextMentions = mentions ?? previous.mentions;
      set((state) => ({
        comments: state.comments.map((c) =>
          c.id === commentId
            ? { ...c, body, mentions: nextMentions, updatedAt: now, isEdited: true }
            : c,
        ),
      }));

      if (isSupabaseAvailable()) {
        const db = getTypedSupabaseClient();
        void db
          .from(TABLE_NAME)
          .update({
            body,
            mentions: nextMentions,
            is_edited: true,
            updated_at: now.toISOString(),
          })
          .eq('id', commentId)
          .then(({ error }) => {
            if (error) {
              console.error('[EntityCommentsStore] update error:', error.message);
              set((state) => ({
                comments: state.comments.map((c) => (c.id === commentId ? previous : c)),
              }));
              dispatchStoreError('EntityComments', 'edit comment', error.message);
            }
          });
      }
    },

    // -------------------------------------------------------------------------
    deleteComment: (commentId) => {
      const removed = get().comments.filter((c) => c.id === commentId || c.replyToId === commentId);
      if (removed.length === 0) return;
      set((state) => ({
        comments: state.comments.filter((c) => c.id !== commentId && c.replyToId !== commentId),
      }));

      if (isSupabaseAvailable()) {
        const db = getTypedSupabaseClient();
        void db
          .from(TABLE_NAME)
          .delete()
          .eq('id', commentId)
          .then(({ error }) => {
            if (error) {
              console.error('[EntityCommentsStore] delete error:', error.message);
              set((state) => ({ comments: [...state.comments, ...removed] }));
              dispatchStoreError('EntityComments', 'delete comment', error.message);
            }
          });
      }
    },

    // -------------------------------------------------------------------------
    setThreadResolved: (commentId, resolved, byUserId) => {
      const previous = get().comments.find((c) => c.id === commentId);
      if (!previous || previous.replyToId) return;
      const now = new Date();
      set((state) => ({
        comments: state.comments.map((c) =>
          c.id === commentId
            ? { ...c, resolvedAt: resolved ? now : null, resolvedBy: resolved ? byUserId : null }
            : c,
        ),
      }));

      if (isSupabaseAvailable()) {
        // Only resolved_at travels: the server stamps resolved_by and the time
        // (815, ec_guard_update), and a member who is not the author may change
        // nothing else.
        const db = getTypedSupabaseClient();
        void db
          .from(TABLE_NAME)
          .update({ resolved_at: resolved ? now.toISOString() : null })
          .eq('id', commentId)
          .then(({ error }) => {
            if (error) {
              console.error('[EntityCommentsStore] resolve error:', error.message);
              set((state) => ({
                comments: state.comments.map((c) => (c.id === commentId ? previous : c)),
              }));
              dispatchStoreError(
                'EntityComments',
                resolved ? 'resolve thread' : 'reopen thread',
                error.message,
              );
            }
          });
      }
    },

    // -------------------------------------------------------------------------
    getCommentsForEntity: (entityType, entityId) =>
      get()
        .comments.filter((c) => sameEntity(c, entityType, entityId) && !c.replyToId)
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),

    getReplies: (commentId) =>
      get()
        .comments.filter((c) => c.replyToId === commentId)
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()),

    getCommentCount: (entityType, entityId) =>
      get().comments.filter((c) => sameEntity(c, entityType, entityId)).length,

    getThreadCounts: (entityType, entityId, anchorKey) => {
      let open = 0;
      let resolved = 0;
      for (const c of get().comments) {
        if (!sameEntity(c, entityType, entityId) || c.replyToId) continue;
        if (anchorKey !== undefined && c.anchorKey !== anchorKey) continue;
        if (c.resolvedAt) resolved += 1;
        else open += 1;
      }
      return { open, resolved };
    },
  }),
);
